"use client";

import { cn } from "@/lib/cn";
import { useTheme } from "next-themes";
import { useState, useRef } from "react";
import { flushSync } from "react-dom";
import { MdModeNight, MdSunny } from "react-icons/md";

type props = {
  className?: string;
};

export const AnimatedThemeToggler = ({ className }: props) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { setTheme } = useTheme();

  const changeTheme = async () => {
    if (!buttonRef.current) return;

    await document.startViewTransition(() => {
      flushSync(() => {
        const dark = document.documentElement.classList.toggle("dark");
        setIsDarkMode(dark);
        setTheme(dark ? "dark" : "light");
      });
    }).ready;

    const { top, left, width, height } =
      buttonRef.current.getBoundingClientRect();
    const y = top + height / 2;
    const x = left + width / 2;

    const right = window.innerWidth - left;
    const bottom = window.innerHeight - top;
    const maxRad = Math.hypot(Math.max(left, right), Math.max(top, bottom));

    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${maxRad}px at ${x}px ${y}px)`,
        ],
      },
      {
        duration: 700,
        easing: "ease-in-out",
        pseudoElement: "::view-transition-new(root)",
      }
    );
  };
  return (
    <button
      aria-label="Toggle Theme"
      ref={buttonRef}
      onClick={changeTheme}
      className={cn(className)}
    >
      {isDarkMode ? (
        <MdSunny className="cursor-pointer text-2xl" />
      ) : (
        <MdModeNight className="cursor-pointer text-2xl" />
      )}
    </button>
  );
};
